/**
 * Anonymous → permanent account merge for the guest demo flow.
 *
 * The demo flow's save gate (Beat 5) promises "your streak is waiting" — that
 * promise is a lie unless every byte of the anon user's data ends up under the
 * new account's UID. Two paths:
 *
 *   Path A (no collision): linkWithCredential(anonUser, credential)
 *     The UID is preserved; all Firestore data already lives under it. We
 *     only need to refresh the device_map mapping (now non-anonymous) and
 *     drop a few localStorage breadcrumbs.
 *
 *   Path B (auth/credential-already-in-use): the email/Google account already
 *     exists. We can't link, so:
 *       1. Read every demo-relevant doc under users/{anonUid} (decks, cards,
 *          progress).
 *       2. Sign out the anon user, sign in to the existing account.
 *       3. Write all those docs into users/{realUid}, then delete the
 *          anon-side copies.
 *       4. Rewrite device_map/{fingerprint} → realUid.
 *     Conflicts: if a deck of the same name exists, we still write the demo
 *     deck (a fresh deck doc id) — the merge is additive, never destructive
 *     to the existing account.
 *
 * Streak / level / EXP all derive from the deck + card + progress docs (see
 * SpacedRepetition.getOverallStats and LevelingSystem), so copying those
 * collections carries the user-visible state implicitly.
 */

import {
  EmailAuthProvider,
  GoogleAuthProvider,
  fetchSignInMethodsForEmail,
  linkWithCredential,
  linkWithPopup,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';

import { auth, db } from '../firebase/firebaseConfig';

const DEVICE_MAP_COLLECTION = 'device_map';
const LS_FP_KEY = 'fc:deviceFp';
const LS_UID_KEY = 'fc:anonUid';
const LS_DAILY_SESSION_PREFIX = 'flashcode_daily_session_';

let accountMergeDepth = 0;

/** True while mergeAnonymousAccount is switching anon → permanent auth. */
export function isAccountMergeInProgress() {
  return accountMergeDepth > 0;
}

const safeLocalStorage = {
  get(key) {
    try {
      return typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      if (typeof window === 'undefined') return;
      if (value == null) window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, value);
    } catch {
      /* ignore */
    }
  },
};

/**
 * Rewrite the device_map mapping after a successful merge so the device's
 * fingerprint now points at the real (permanent) UID, not the anon UID.
 * Idempotent and safe to call when fingerprint isn't available.
 */
async function rewriteDeviceMap(realUid) {
  const fp = safeLocalStorage.get(LS_FP_KEY);
  if (!fp || !realUid) return;
  try {
    const mapRef = doc(db, DEVICE_MAP_COLLECTION, fp);
    const batch = writeBatch(db);
    batch.set(mapRef, {
      uid: realUid,
      isAnonymous: false,
      mergedAt: serverTimestamp(),
      userAgent:
        typeof navigator !== 'undefined'
          ? String(navigator.userAgent || '').slice(0, 256)
          : null,
    });
    await batch.commit();
  } catch (err) {
    console.warn('[accountMerge] rewriteDeviceMap failed:', err);
  }
}

/**
 * Carry the daily-session localStorage flag from the anon UID namespace to the
 * real UID namespace so today's daily_session GA event isn't double-counted.
 */
function carryDailySessionLocalStorage(anonUid, realUid) {
  if (!anonUid || !realUid || anonUid === realUid) return;
  const anonKey = `${LS_DAILY_SESSION_PREFIX}${anonUid}`;
  const realKey = `${LS_DAILY_SESSION_PREFIX}${realUid}`;
  const value = safeLocalStorage.get(anonKey);
  if (value) safeLocalStorage.set(realKey, value);
  safeLocalStorage.set(anonKey, null);
}

/**
 * Read a complete snapshot of the anon user's demo-relevant Firestore tree.
 * We only copy collections we own: decks (+ cards subcollection) and
 * progress. The user has just played 5 reps, so this is small.
 */
async function snapshotAnonData(anonUid) {
  const decksSnap = await getDocs(collection(db, `users/${anonUid}/decks`));
  const decks = [];
  for (const deckDoc of decksSnap.docs) {
    const cardsSnap = await getDocs(
      collection(db, `users/${anonUid}/decks/${deckDoc.id}/cards`)
    );
    decks.push({
      id: deckDoc.id,
      data: deckDoc.data(),
      cards: cardsSnap.docs.map((c) => ({ id: c.id, data: c.data() })),
    });
  }

  const progressSnap = await getDocs(collection(db, `users/${anonUid}/progress`));
  const progress = progressSnap.docs.map((p) => ({ id: p.id, data: p.data() }));

  const userDocSnap = await getDoc(doc(db, `users/${anonUid}`));
  const userDoc = userDocSnap.exists() ? userDocSnap.data() : null;

  return { decks, progress, userDoc };
}

/**
 * Write the snapshot under the real UID. Doc ids are preserved (so progress
 * docs keyed `${deckId}_${cardId}` still match their cards). Batches are kept
 * under 400 ops to stay below Firestore's 500-op cap.
 */
async function writeSnapshotToUid(realUid, snapshot) {
  const ops = [];

  if (snapshot.userDoc) {
    ops.push(['set', doc(db, `users/${realUid}`), snapshot.userDoc, { merge: true }]);
  }

  for (const deck of snapshot.decks) {
    ops.push(['set', doc(db, `users/${realUid}/decks/${deck.id}`), deck.data]);
    for (const card of deck.cards) {
      ops.push([
        'set',
        doc(db, `users/${realUid}/decks/${deck.id}/cards/${card.id}`),
        card.data,
      ]);
    }
  }
  for (const p of snapshot.progress) {
    ops.push(['set', doc(db, `users/${realUid}/progress/${p.id}`), p.data]);
  }

  await runBatched(ops);
}

/**
 * Delete the anon-side copies after a successful copy-then-delete merge.
 * Only the demo collections we wrote — keeping device_map separate (handled
 * by rewriteDeviceMap).
 */
async function deleteAnonData(anonUid, snapshot) {
  const ops = [];
  for (const deck of snapshot.decks) {
    for (const card of deck.cards) {
      ops.push(['delete', doc(db, `users/${anonUid}/decks/${deck.id}/cards/${card.id}`)]);
    }
    ops.push(['delete', doc(db, `users/${anonUid}/decks/${deck.id}`)]);
  }
  for (const p of snapshot.progress) {
    ops.push(['delete', doc(db, `users/${anonUid}/progress/${p.id}`)]);
  }
  ops.push(['delete', doc(db, `users/${anonUid}`)]);

  await runBatched(ops);
}

async function runBatched(ops, chunkSize = 400) {
  for (let i = 0; i < ops.length; i += chunkSize) {
    const slice = ops.slice(i, i + chunkSize);
    const batch = writeBatch(db);
    for (const op of slice) {
      if (op[0] === 'set') {
        const [, ref, data, options] = op;
        if (options) batch.set(ref, data, options);
        else batch.set(ref, data);
      } else if (op[0] === 'delete') {
        batch.delete(op[1]);
      }
    }
    await batch.commit();
  }
}

const isCollisionError = (err) => {
  const code = err?.code || '';
  return (
    code === 'auth/credential-already-in-use' ||
    code === 'auth/email-already-in-use' ||
    code === 'auth/account-exists-with-different-credential'
  );
};

/**
 * Path A: link the credential to the anon user. Throws on collision so the
 * caller can fall back to copy-then-delete.
 *
 * Returns the now non-anonymous Firebase user (same UID as before).
 */
async function linkPath(provider, credentialFactory) {
  const anonUser = auth.currentUser;
  if (!anonUser || !anonUser.isAnonymous) {
    throw new Error('linkPath: no anonymous user signed in');
  }

  if (provider === 'google') {
    const googleProvider = new GoogleAuthProvider();
    const result = await linkWithPopup(anonUser, googleProvider);
    return result.user;
  }

  const cred = credentialFactory();
  const result = await linkWithCredential(anonUser, cred);
  return result.user;
}

/**
 * Path B: snapshot the anon data, sign in to the existing account, copy the
 * snapshot under the real UID, then delete the anon-side copies. Returns the
 * real (permanent) Firebase user.
 */
async function copyThenDeletePath(
  provider,
  credentialFactory,
  signInWithExisting,
  pendingGoogleCredential = null
) {
  const anonUser = auth.currentUser;
  if (!anonUser || !anonUser.isAnonymous) {
    throw new Error('copyThenDeletePath: no anonymous user signed in');
  }
  const anonUid = anonUser.uid;

  const snapshot = await snapshotAnonData(anonUid);

  // Do not signOut here — App.js would start a new anonymous session while we
  // sign into the existing account, racing writeSnapshot and causing permission-denied.
  let realUser;
  if (provider === 'google') {
    if (pendingGoogleCredential) {
      const result = await signInWithCredential(auth, pendingGoogleCredential);
      realUser = result.user;
    } else {
      const googleProvider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, googleProvider);
      realUser = result.user;
    }
  } else {
    realUser = await signInWithExisting(credentialFactory);
  }

  if (!realUser?.uid) {
    throw new Error('copyThenDeletePath: failed to sign into existing account');
  }
  if (realUser.uid === anonUid) {
    return realUser;
  }

  await writeSnapshotToUid(realUser.uid, snapshot);

  try {
    await deleteAnonData(anonUid, snapshot);
  } catch (err) {
    console.warn('[accountMerge] anon-side cleanup failed (non-fatal):', err);
  }

  return realUser;
}

/**
 * Public entry point. Pass the auth `provider` ('google' | 'password') and
 * (for password) the email/password the user typed in the gate.
 *
 * @param {{ provider: 'google' | 'password', email?: string, password?: string }} params
 * @returns {Promise<{ user: import('firebase/auth').User, merged: boolean, path: 'link' | 'copy' }>}
 */
export async function mergeAnonymousAccount(params) {
  accountMergeDepth += 1;
  try {
  const { provider } = params;

  const credentialFactory =
    provider === 'password'
      ? () => EmailAuthProvider.credential(params.email, params.password)
      : null;

  const signInWithExisting =
    provider === 'password'
      ? async () => {
          const result = await signInWithEmailAndPassword(
            auth,
            params.email,
            params.password
          );
          return result.user;
        }
      : null;

  const anonUid = auth.currentUser?.isAnonymous ? auth.currentUser.uid : null;

  if (provider === 'password' && params.email) {
    try {
      const methods = await fetchSignInMethodsForEmail(auth, params.email);
      if (methods.length > 0) {
        const realUser = await copyThenDeletePath(
          provider,
          credentialFactory,
          signInWithExisting
        );
        carryDailySessionLocalStorage(anonUid, realUser.uid);
        await rewriteDeviceMap(realUser.uid);
        safeLocalStorage.set(LS_UID_KEY, null);
        return { user: realUser, merged: true, path: 'copy' };
      }
    } catch (probeErr) {
      console.warn('[accountMerge] fetchSignInMethodsForEmail failed; falling through to link:', probeErr);
    }
  }

  try {
    const linkedUser = await linkPath(provider, credentialFactory);
    await rewriteDeviceMap(linkedUser.uid);
    return { user: linkedUser, merged: true, path: 'link' };
  } catch (err) {
    if (!isCollisionError(err)) throw err;

    const pendingGoogleCredential =
      provider === 'google' ? GoogleAuthProvider.credentialFromError(err) : null;

    const realUser = await copyThenDeletePath(
      provider,
      credentialFactory,
      signInWithExisting,
      pendingGoogleCredential
    );
    carryDailySessionLocalStorage(anonUid, realUser.uid);
    await rewriteDeviceMap(realUser.uid);
    safeLocalStorage.set(LS_UID_KEY, null);
    return { user: realUser, merged: true, path: 'copy' };
  }
  } finally {
    accountMergeDepth -= 1;
  }
}

export default { mergeAnonymousAccount };
